import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import cn from "classnames";
import styles from "./CreatorDashboard.module.sass";
import { useChatContext } from "../../contexts/ChatContext";
import InfoPage from "../InfoPage";
import Settings from "../../components/Settings";
import Icon from "../../components/Icon";
import CreatorReviews from "../../components/CreatorReviews";
import PendingReviews from "../../components/PendingReviews";
import { uploadFile } from "../../utils/fileUpload";
import LocationAutocomplete from "../../components/LocationAutocomplete";
import { usePersona } from "../../contexts/PersonaContext";
import IntroVideoUpload from "../../components/IntroVideoUpload";
import ShowcaseUpload from "../../components/ShowcaseUpload";

const Card = ({ img, handle, fallback, onClick }) => (
    <div 
        style={{ display: 'grid', justifyItems: 'stretch', gap: 8, width: '100%', cursor: onClick ? 'pointer' : 'default' }}
        onClick={onClick}
    >
        <img
            src={img}
            alt={handle}
            referrerPolicy="no-referrer"
            crossOrigin="anonymous"
            onError={(e) => {
                if (fallback && e?.currentTarget?.src !== window.location.origin + fallback && !e.currentTarget.dataset.fallbackApplied) {
                    e.currentTarget.dataset.fallbackApplied = '1';
                    e.currentTarget.src = fallback;
                }
            }}
            style={{ width: '100%', aspectRatio: '3 / 4', height: 'auto', objectFit: 'cover', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,.08)', display: 'block', transition: 'transform 0.2s, box-shadow 0.2s' }}
            onMouseEnter={(e) => { if (onClick) { e.currentTarget.style.transform = 'scale(1.02)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,.12)'; }}}
            onMouseLeave={(e) => { if (onClick) { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,.08)'; }}}
        />
        <div style={{ fontFamily: "'Neue Einstellung', 'Poppins', sans-serif", fontSize: 12, color: '#0f084b', opacity: .9 }}>{handle}</div>
    </div>
);

const NavigationButton = ({ direction, onClick, show }) => {
    if (!show) return null;
    return (
        <motion.button
            onClick={onClick}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            transition={{ duration: 0.2 }}
            style={{
                position: 'absolute',
                top: '50%',
                transform: 'translateY(-50%)',
                [direction === 'left' ? 'left' : 'right']: -20,
                width: 40,
                height: 40,
                borderRadius: '50%',
                backgroundColor: '#dc1d08',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                zIndex: 10
            }}
        >
            <svg 
                width="16" 
                height="16" 
                viewBox="0 0 16 16" 
                fill="none" 
                style={{ transform: direction === 'left' ? 'rotate(180deg)' : 'none' }}
            >
                <path 
                    d="M6 4L10 8L6 12" 
                    stroke="white" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                />
            </svg>
        </motion.button>
    );
};

const breadcrumbs = [
    { title: "Home", url: "/" },
    { title: "Creator Dashboard" },
];

const menuItems = [
    { title: "Overview", icon: "home" },
    { title: "Profile", icon: "user" },
    { title: "Itineraries", icon: "map" },
    { title: "Messages", icon: "comment" },
    { title: "Collaborations", icon: "briefcase" },
    { title: "Reviews", icon: "star" },
    { title: "Analytics", icon: "bar-chart" },
    { title: "Settings", icon: "settings" },
];

// Categories and preferences constants
const CATEGORIES = [
    { key: "travel", label: "Travel" },
    { key: "lifestyle", label: "Lifestyle" },
    { key: "parenting_family", label: "Parenting/Family" },
    { key: "food", label: "Food" },
    { key: "fashion_beauty", label: "Fashion/Beauty" },
    { key: "health_fitness", label: "Health & Fitness" },
    { key: "professional_photographer", label: "Professional Photographer" },
    { key: "drone_footage", label: "Drone Footage" },
    { key: "other", label: "Other" },
];

const PREFS = [
    { key: "paid_only", label: "Accepting Paid Partnerships Only" },
    { key: "gifted_and_paid", label: "Accepting Gifted & Paid Partnerships" },
    { key: "require_pet_friendly", label: "Require Pet Friendly Accommodations" },
    { key: "flexible_last_minute", label: "Flexible and willing to travel last minute" },
    { key: "require_advanced_notice", label: "Require advanced notice to travel" },
    { key: "willing_weekdays", label: "Willing to travel/stay on weekdays" },
    { key: "weekends_only", label: "Available on weekends only" },
];

// Normalize a URL: trim, force https, add www. only for bare two-label hostnames
const normalizeUrl = (raw) => {
    const trimmed = (raw || '').trim();
    if (!trimmed) return '';
    // Prepend scheme if missing
    let withScheme = trimmed;
    if (!/^https?:\/\//i.test(withScheme)) {
        withScheme = 'https://' + withScheme;
    }
    try {
        const parsed = new URL(withScheme);
        // Force https
        parsed.protocol = 'https:';
        const hostname = parsed.hostname;
        // Add www. only when hostname has exactly two labels (e.g. "instagram.com")
        // and does not already start with "www."
        const labels = hostname.split('.');
        if (labels.length === 2 && !hostname.startsWith('www.')) {
            parsed.hostname = 'www.' + hostname;
        }
        return parsed.toString().replace(/\/$/, '') || parsed.toString();
    } catch {
        // If URL parsing fails, return the trimmed input as-is
        return trimmed;
    }
};

const CreatorDashboard = () => {
    const navigate = useNavigate();
    const { activePersona, switchPersona } = usePersona();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    const { initializeChat, isConnected } = useChatContext();
    
    const [activeTab, setActiveTab] = useState(() => {
        if (tabParam) {
            const tabMap = {
                'profile': 'Profile',
                'itineraries': 'Itineraries',
                'messages': 'Messages',
                'collaborations': 'Collaborations',
                'reviews': 'Reviews',
                'analytics': 'Analytics',
                'settings': 'Settings'
            };
            return tabMap[tabParam.toLowerCase()] || 'Overview';
        }
        return 'Overview';
    });
    
    const [loading, setLoading] = useState(false);
    const [collabHosts, setCollabHosts] = useState([]);
    const [hostStartIndex, setHostStartIndex] = useState(0);
    const [hostDirection, setHostDirection] = useState(1);
    
    // Creator profile state
    const [creatorProfile, setCreatorProfile] = useState(null);
    const [profileForm, setProfileForm] = useState({
        firstName: '',
        lastName: '',
        handle: '',
        bio: '',
        locationCountry: '',
        locationCity: '',
        locationState: '',
        ageRange: '',
        gender: '',
        instagram: '',
        tiktok: '',
        youtube: '',
        other: '',
        categories: [],
        nicheOther: '',
        preferences: [],
        partners: [],
        partnerInput: '',
        avatarUrl: '',
        introVideo: null,
        mediaKitUrl: ''
    });
    const [avatarFile, setAvatarFile] = useState(null);
    const [avatarUploading, setAvatarUploading] = useState(false);
    const [avatarUploadSuccess, setAvatarUploadSuccess] = useState(false);
    const [savingProfile, setSavingProfile] = useState(false);
    const [introVideoSaveStatus, setIntroVideoSaveStatus] = useState('');
    const [showcaseAssets, setShowcaseAssets] = useState([]);
    const [mediaKitFile, setMediaKitFile] = useState(null);
    const [mediaKitUploading, setMediaKitUploading] = useState(false);
    
    // Referral link state for copy functionality
    const [referralUrl, setReferralUrl] = useState(null);
    const [copyStatus, setCopyStatus] = useState('idle'); // 'idle' | 'copying' | 'copied' | 'failed'
    const [showReferralUrl, setShowReferralUrl] = useState(false); // Show URL text field as fallback
    
    // Refs to track focused elements and preserve focus across re-renders
    const bioTextareaRef = useRef(null);
    const stateInputRef = useRef(null);
    const cityInputRef = useRef(null);
    const firstNameInputRef = useRef(null);
    const lastNameInputRef = useRef(null);
    const handleInputRef = useRef(null);
    const partnerInputRef = useRef(null);
    const activeFieldRef = useRef(null); // Track which field was active before state update
    
    const email = typeof window !== 'undefined' ? 
        window.localStorage.getItem('postaway_email') || '' : '';
    const authToken = typeof window !== 'undefined' ?
        window.localStorage.getItem('postaway_auth_token') : null;
    
    const apiBase = useMemo(() => (typeof window !== 'undefined' && window.location.hostname === 'localhost') ? 'http://localhost:4000' : '', []);

    // Pre-fetch referral URL on mount so it's ready for immediate use
    useEffect(() => {
        const fetchReferralUrl = async () => {
            const userEmail = localStorage.getItem('postaway_email') || '';
            if (!userEmail) return;
            try {
                const resp = await fetch(`${apiBase}/api/rewardful/referral-link?email=${encodeURIComponent(userEmail)}`);
                const data = await resp.json();
                if (data.ok && data.url) {
                    setReferralUrl(data.url);
                }
            } catch (err) {
                console.error('Failed to pre-fetch referral URL:', err);
            }
        };
        fetchReferralUrl();
    }, [apiBase]);

    // Helper to copy text to clipboard with mobile-friendly fallback
    const copyToClipboard = useCallback(async (text) => {
        // Try modern clipboard API first
        if (navigator.clipboard && navigator.clipboard.writeText) {
            try {
                await navigator.clipboard.writeText(text);
                return true;
            } catch (err) {
                console.warn('Clipboard API failed:', err);
            }
        }
        // Fallback for mobile and older browsers
        try {
            const textarea = document.createElement('textarea');
            textarea.value = text;
            textarea.setAttribute('readonly', '');
            textarea.style.position = 'absolute';
            textarea.style.left = '-9999px';
            textarea.style.top = '0';
            document.body.appendChild(textarea);
            
            // iOS specific handling
            const range = document.createRange();
            range.selectNodeContents(textarea);
            const selection = window.getSelection();
            selection.removeAllRanges();
            selection.addRange(range);
            textarea.setSelectionRange(0, 999999);
            
            const success = document.execCommand('copy');
            document.body.removeChild(textarea);
            return success;
        } catch (err) {
            console.warn('Fallback clipboard copy failed:', err);
            return false;
        }
    }, []);

    // Helper to handle copy referral link button
    const handleCopyReferralLink = useCallback(async () => {
        if (!referralUrl) {
            // URL not loaded yet - show it so user can manually copy
            setShowReferralUrl(true);
            window.open('https://postaway.getrewardful.com/signup', '_blank', 'noopener');
            return;
        }
        
        setCopyStatus('copying');
        const success = await copyToClipboard(referralUrl);
        
        if (success) {
            setCopyStatus('copied');
            setShowReferralUrl(false);
            setTimeout(() => setCopyStatus('idle'), 2000);
        } else {
            // Clipboard failed - show URL field so user can manually copy
            setCopyStatus('failed');
            setShowReferralUrl(true);
            setTimeout(() => setCopyStatus('idle'), 3000);
        }
    }, [referralUrl, copyToClipboard]);

    // Handle Invite a Friend click - uses cached URL
    const handleInviteFriend = useCallback(() => {
        if (referralUrl) {
            const subject = encodeURIComponent('Join me on Postaway');
            const body = encodeURIComponent(`Hey! Check out Postaway – my referral link: ${referralUrl}`);
            window.location.href = `mailto:?subject=${subject}&body=${body}`;
        } else {
            // No referral URL - open signup
            window.open('https://postaway.getrewardful.com/signup', '_blank', 'noopener');
        }
    }, [referralUrl]);

    // Sync activePersona to 'creator' as a side effect when the dashboard
    // mounts (and again only if the persona drifts away from 'creator'). Kept
    // in its own effect so the subscription/chat init effect below doesn't
    // re-fire every time switchPersona's identity changes.
    useEffect(() => {
        if (!email) return;
        if (activePersona && activePersona !== 'creator') {
            switchPersona('creator').catch(() => {});
        }
    }, [email, activePersona, switchPersona]);

    useEffect(() => {
        if (!email) {
            navigate('/login');
            return;
        }

        // Check subscription status before allowing dashboard access
        (async () => {
            try {
                const res = await fetch('/api/access/subscription', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email })
                });
                const data = await res.json();
                
                if (!res.ok || !data.ok || !data.active || !(data.roles || []).includes('creator')) {
                    // No active subscription - check if user has completed profile
                    console.warn('Creator dashboard: No active subscription');
                    try {
                        const profileRes = await fetch(`/api/creator/profile?email=${encodeURIComponent(email)}`);
                        const profileData = await profileRes.json();
                        
                        if (profileData.ok && profileData.profile && profileData.profile.profile_id) {
                            // User has profile - skip to checkout page
                            navigate(`/success?email=${encodeURIComponent(email)}`);
                        } else {
                            // No profile - send to onboarding first
                            navigate(`/creator/onboarding?email=${encodeURIComponent(email)}`);
                        }
                    } catch (profileError) {
                        console.error('Profile check error:', profileError);
                        // On error, default to onboarding
                        navigate(`/creator/onboarding?email=${encodeURIComponent(email)}`);
                    }
                    return;
                }
                
                // User has active subscription - initialize chat if needed
                if (authToken && !isConnected) {
                    initializeChat(authToken).catch(console.error);
                }
            } catch (error) {
                console.error('Subscription check error:', error);
                // On error, redirect to onboarding to be safe
                navigate(`/creator/onboarding?email=${encodeURIComponent(email)}`);
            }
        })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [email, authToken, isConnected, initializeChat, navigate]);

    // Update tab based on URL parameter changes
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const tab = params.get('tab');
        if (tab) {
            const tabMap = {
                'profile': 'Profile',
                'itineraries': 'Itineraries',
                'messages': 'Messages',
                'collaborations': 'Collaborations',
                'reviews': 'Reviews',
                'analytics': 'Analytics',
                'settings': 'Settings'
            };
            const mappedTab = tabMap[tab.toLowerCase()];
            if (mappedTab) {
                setActiveTab(mappedTab);
            }
        } else {
            setActiveTab('Overview');
        }
    }, [location.search]);
    
    // Fetch collab hosts for opportunities
    // Fetch creator profile
    useEffect(() => {
        const fetchCreatorProfile = async () => {
            if (!email) return;
            try {
                const response = await fetch(`${apiBase}/api/creator/profile?email=${encodeURIComponent(email)}`);
                if (response.ok) {
                    const data = await response.json();
                    if (data.profile) {
                        setCreatorProfile(data.profile);
                        
                        // Normalize categories (niches) - handle PostgreSQL array strings
                        let categoriesArray = [];
                        if (data.profile.niches) {
                            if (Array.isArray(data.profile.niches)) {
                                categoriesArray = data.profile.niches;
                            } else if (typeof data.profile.niches === 'string') {
                                if (data.profile.niches.startsWith('{') && data.profile.niches.endsWith('}')) {
                                    // PostgreSQL array format: {item1,item2,item3}
                                    categoriesArray = data.profile.niches.slice(1, -1).split(',').map(c => c.trim().replace(/^"|"$/g, '')).filter(c => c);
                                } else {
                                    try {
                                        const parsed = JSON.parse(data.profile.niches);
                                        categoriesArray = Array.isArray(parsed) ? parsed : [];
                                    } catch (e) {
                                        categoriesArray = [];
                                    }
                                }
                            }
                        }
                        
                        // Normalize preferences - handle PostgreSQL array strings
                        let preferencesArray = [];
                        if (data.profile.preferences) {
                            if (Array.isArray(data.profile.preferences)) {
                                preferencesArray = data.profile.preferences;
                            } else if (typeof data.profile.preferences === 'string') {
                                if (data.profile.preferences.startsWith('{') && data.profile.preferences.endsWith('}')) {
                                    // PostgreSQL array format: {item1,item2,item3}
                                    preferencesArray = data.profile.preferences.slice(1, -1).split(',').map(p => p.trim().replace(/^"|"$/g, '')).filter(p => p);
                                } else {
                                    try {
                                        const parsed = JSON.parse(data.profile.preferences);
                                        preferencesArray = Array.isArray(parsed) ? parsed : [];
                                    } catch (e) {
                                        preferencesArray = [];
                                    }
                                }
                            }
                        }
                        
                        // Normalize partners - handle PostgreSQL array strings
                        let partnersArray = [];
                        if (data.profile.partners) {
                            if (Array.isArray(data.profile.partners)) {
                                partnersArray = data.profile.partners;
                            } else if (typeof data.profile.partners === 'string') {
                                if (data.profile.partners.startsWith('{') && data.profile.partners.endsWith('}')) {
                                    // PostgreSQL array format: {item1,item2,item3}
                                    partnersArray = data.profile.partners.slice(1, -1).split(',').map(p => p.trim().replace(/^"|"$/g, '')).filter(p => p);
                                } else {
                                    try {
                                        const parsed = JSON.parse(data.profile.partners);
                                        partnersArray = Array.isArray(parsed) ? parsed : [];
                                    } catch (e) {
                                        partnersArray = [];
                                    }
                                }
                            }
                        }
                        
                        // Initialize form with fetched data
                        setProfileForm({
                            firstName: data.profile.first_name || '',
                            lastName: data.profile.last_name || '',
                            handle: data.profile.handle || '',
                            bio: data.profile.bio || '',
                            locationCountry: data.profile.location_country || '',
                            locationCity: data.profile.location_city || '',
                            locationState: data.profile.location_state || '',
                            ageRange: data.profile.age_range || '',
                            gender: data.profile.gender || '',
                            instagram: data.profile.socials?.instagram?.url || data.profile.socials?.instagram || '',
                            tiktok: data.profile.socials?.tiktok?.url || data.profile.socials?.tiktok || '',
                            youtube: data.profile.socials?.youtube?.url || data.profile.socials?.youtube || '',
                            other: data.profile.socials?.other?.url || data.profile.socials?.other || '',
                            categories: categoriesArray,
                            nicheOther: data.profile.niche_other || '',
                            preferences: preferencesArray,
                            partners: partnersArray,
                            avatarUrl: data.profile.avatar_url || '',
                            introVideo: data.profile.intro_video_url ? {
                                url: data.profile.intro_video_url,
                                hlsUrl: data.profile.intro_video_hls_url || '',
                                posterUrl: data.profile.intro_video_poster_url || '',
                                publicId: data.profile.intro_video_public_id || '',
                                duration: data.profile.intro_video_duration_sec || null,
                            } : null,
                            mediaKitUrl: data.profile.media_kit_url || ''
                        });
                    }
                }
            } catch (error) {
                console.error('Failed to fetch creator profile:', error);
            }
        };

        const fetchHostProperties = async () => {
            try {
                const response = await fetch(`${apiBase}/api/collab/board`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email })
                });
                if (response.ok) {
                    const data = await response.json();
                    const hosts = data.hosts || [];
                    setCollabHosts(hosts);
                } else {
                    setCollabHosts([]);
                }
            } catch (error) {
                console.error('Failed to fetch host properties:', error);
            }
        };
        
        fetchCreatorProfile();
        fetchHostProperties();
    }, [apiBase, email]);

    useEffect(() => {
        const profileId = creatorProfile?.profile_id;
        if (!profileId) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(`${apiBase}/api/showcase?profile_kind=creator&creator_profile_id=${encodeURIComponent(profileId)}`);
                if (!res.ok) return;
                const data = await res.json();
                if (!cancelled && data?.ok) {
                    setShowcaseAssets(Array.isArray(data.assets) ? data.assets : []);
                }
            } catch (err) {
                console.error('Failed to fetch showcase assets:', err);
            }
        })();
        return () => { cancelled = true; };
    }, [apiBase, creatorProfile?.profile_id]);

    const handleHostPrev = () => {
        if (hostStartIndex <= 0) return;
        setHostDirection(-1);
        setHostStartIndex((prev) => Math.max(0, prev - 5));
    };
    const handleHostNext = () => {
        if (hostStartIndex + 5 >= collabHosts.length) return;
        setHostDirection(1);
        setHostStartIndex((prev) => Math.min(collabHosts.length - 5, prev + 5));
    };
    
    // Profile form handlers - memoized to prevent unnecessary re-renders
    const handleProfileChange = useCallback((field, value) => {
        const activeElementBefore = document.activeElement;
        const isInput = activeElementBefore.tagName === 'INPUT' || activeElementBefore.tagName === 'TEXTAREA';
        
        // Store which field was active before state update
        if (isInput) {
            activeFieldRef.current = {
                field,
                id: activeElementBefore.id || null,
                tagName: activeElementBefore.tagName,
                selectionStart: activeElementBefore.selectionStart,
                selectionEnd: activeElementBefore.selectionEnd,
            };
        }
        
        setProfileForm(prev => ({ ...prev, [field]: value }));
    }, []);

    const handleCountryChange = useCallback((value) => {
        const activeElementBefore = document.activeElement;
        const isInput = activeElementBefore.tagName === 'INPUT' || activeElementBefore.tagName === 'TEXTAREA';

        if (isInput) {
            activeFieldRef.current = {
                field: 'locationCountry',
                id: activeElementBefore.id || null,
                tagName: activeElementBefore.tagName,
                selectionStart: activeElementBefore.selectionStart,
                selectionEnd: activeElementBefore.selectionEnd,
            };
        }

        setProfileForm(prev => ({
            ...prev,
            locationCountry: value,
            locationState: value !== prev.locationCountry ? '' : prev.locationState,
            locationCity: value !== prev.locationCountry ? '' : prev.locationCity,
        }));
    }, []);
    
    const handleCategoryToggle = useCallback((key) => {
        setProfileForm(prev => {
            const isSelected = prev.categories.includes(key);
            if (isSelected) {
                // Allow removing selected categories
                return {
                    ...prev,
                    categories: prev.categories.filter(c => c !== key)
                };
            } else {
                // Only allow adding if less than 3 are selected
                if (prev.categories.length >= 3) {
                    return prev; // Don't change if limit reached
                }
                return {
                    ...prev,
                    categories: [...prev.categories, key]
                };
            }
        });
    }, []);
    
    const handlePreferenceToggle = useCallback((key) => {
        setProfileForm(prev => ({
            ...prev,
            preferences: prev.preferences.includes(key)
                ? prev.preferences.filter(p => p !== key)
                : [...prev.preferences, key]
        }));
    }, []);
    
    const handleAddPartner = useCallback(() => {
        const value = profileForm.partnerInput.trim();
        if (!value) return;
        setProfileForm(prev => ({
            ...prev,
            partners: [...prev.partners, value],
            partnerInput: ''
        }));
    }, [profileForm.partnerInput]);
    
    const handleRemovePartner = useCallback((index) => {
        setProfileForm(prev => ({
            ...prev,
            partners: prev.partners.filter((_, i) => i !== index)
        }));
    }, []);

    // Normalize a URL field on blur so the user sees the cleaned value
    const handleUrlBlur = useCallback((field) => {
        setProfileForm(prev => {
            const raw = prev[field];
            if (!raw) return prev;
            const normalized = normalizeUrl(raw);
            if (normalized === raw) return prev;
            return { ...prev, [field]: normalized };
        });
    }, []);
    
    const handleAvatarChange = async (e) => {
        const file = e.target.files?.[0];
        if (file) {
            setAvatarFile(file);
            setAvatarUploading(true);
            setAvatarUploadSuccess(false);
            
            try {
                // Upload file directly (handles HEIC conversion)
                const url = await uploadFile(file);
                setProfileForm(prev => ({ ...prev, avatarUrl: url }));
                setAvatarFile(null); // Clear file after successful upload
                setAvatarUploadSuccess(true);
                
                // Hide checkmark after 2 seconds
                setTimeout(() => setAvatarUploadSuccess(false), 2000);
            } catch (error) {
                console.error('Failed to upload avatar:', error);
            } finally {
                setAvatarUploading(false);
            }
        }
    };

    const handleMediaKitFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        
        if (file.type !== 'application/pdf') {
            alert('Please select a PDF file');
            return;
        }
        
        if (file.size > 50 * 1024 * 1024) {
            alert('PDF file size must be less than 50MB');
            return;
        }
        
        setMediaKitUploading(true);
        try {
            const url = await uploadFile(file);
            handleProfileChange('mediaKitUrl', url);
            setMediaKitFile(null);
        } catch (error) {
            alert('Failed to upload media kit PDF. Please try again.');
        } finally {
            setMediaKitUploading(false);
        }
    };
    
    // Restore focus after profileForm state updates
    useEffect(() => {
        if (!activeFieldRef.current) return;
        
        const { id, selectionStart, selectionEnd } = activeFieldRef.current;
        const currentActive = document.activeElement;
        
        // Only restore if focus was lost (went to BODY or document)
        // Don't restore if user intentionally moved focus to another input/textarea
        const focusLost = currentActive.tagName === 'BODY' || 
                         currentActive === document.body || 
                         currentActive === document;
        
        // If user moved to a different input, clear the ref
        const movedToDifferentInput = currentActive.tagName === 'INPUT' || currentActive.tagName === 'TEXTAREA';
        if (movedToDifferentInput && currentActive.id !== id) {
            activeFieldRef.current = null;
            return;
        }
        
        if (focusLost && id) {
            // Use requestAnimationFrame to ensure DOM has updated after React re-render
            requestAnimationFrame(() => {
                const targetElement = document.getElementById(id);
                if (targetElement && (targetElement.tagName === 'INPUT' || targetElement.tagName === 'TEXTAREA')) {
                    // Double-check that focus is still lost before restoring
                    const stillLost = document.activeElement.tagName === 'BODY' || 
                                    document.activeElement === document.body;
                    
                    if (stillLost) {
                        targetElement.focus();
                        
                        // Restore cursor position if it was saved
                        if (typeof selectionStart === 'number' && typeof selectionEnd === 'number') {
                            try {
                                if (targetElement.setSelectionRange) {
                                    targetElement.setSelectionRange(selectionStart, selectionEnd);
                                }
                            } catch (e) {
                                // Ignore errors setting selection range
                            }
                        }
                        
                        // Focus restored successfully
                    }
                }
            });
        }
    }, [profileForm]);
    
    const handleSaveProfile = async (formOverride = profileForm, options = {}) => {
        const form = formOverride || profileForm;
        const silent = Boolean(options.silent);
        setSavingProfile(true);
        try {
            // Avatar already uploaded in handleAvatarChange
            let avatarUrl = form.avatarUrl;
            let mediaKitUrl = normalizeUrl(form.mediaKitUrl);
            
            const socials = {
                instagram: normalizeUrl(form.instagram) || null,
                tiktok: normalizeUrl(form.tiktok) || null,
                youtube: normalizeUrl(form.youtube) || null,
                other: normalizeUrl(form.other) || null,
            };
            
            const response = await fetch(`${apiBase}/api/creator/profile`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email,
                    firstName: form.firstName,
                    lastName: form.lastName,
                    handle: form.handle,
                    bio: form.bio,
                    locationCountry: form.locationCountry,
                    locationCity: form.locationCity,
                    locationState: form.locationState,
                    ageRange: form.ageRange,
                    gender: form.gender,
                    socials,
                    categories: form.categories,
                    nicheOther: form.categories.includes('other') ? form.nicheOther : null,
                    preferences: form.preferences,
                    partners: form.partners,
                    avatarUrl,
                    introVideo: form.introVideo,
                    mediaKitUrl
                })
            });
            
            if (response.ok) {
                if (!silent) {
                    alert('Profile updated successfully!');
                }
                // Refresh profile data
                const data = await response.json();
                if (data.profile) {
                    setCreatorProfile(data.profile);
                }
                return true;
            } else {
                throw new Error('Failed to save profile');
            }
        } catch (error) {
            console.error('Error saving profile:', error);
            if (!silent) {
                alert('Failed to save profile. Please try again.');
            }
            return false;
        } finally {
            setSavingProfile(false);
        }
    };

    const handleIntroVideoChange = async (introVideo) => {
        const nextForm = { ...profileForm, introVideo };
        setProfileForm(nextForm);
        setIntroVideoSaveStatus(introVideo ? 'Saving intro video...' : 'Removing intro video...');
        const ok = await handleSaveProfile(nextForm, { silent: true });
        setIntroVideoSaveStatus(ok ? (introVideo ? 'Intro video saved.' : 'Intro video removed.') : 'Intro video could not be saved. Please try again.');
        setTimeout(() => setIntroVideoSaveStatus(''), 3000);
    };

    const dashboardContent = (
        <div className={styles.dashboardFullWidth}>
            <div className={styles.dashboardWrapper}>
                <div className={styles.inviteSection}>
                    <Tippy content="We manage our affiliate network through Rewardful. Click here to sign up or view your payout balance.">
                        <span style={{ display: 'inline-flex', alignItems: 'center', color: '#6B7280' }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                                <circle cx="12" cy="12" r="10"/>
                                <path d="M12 16v-4"/>
                                <path d="M12 8h.01"/>
                            </svg>
                        </span>
                    </Tippy>
                    <button
                        type="button"
                        className={cn('button-stroke', styles.action)}
                        style={{ textDecoration: 'none', borderColor: '#0f084b', cursor: 'pointer' }}
                        onMouseEnter={e => {
                            e.currentTarget.style.backgroundColor = '#0f084b';
                            e.currentTarget.style.color = '#ffffff';
                            e.currentTarget.style.borderColor = '#0f084b';
                        }}
                        onMouseLeave={e => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = '';
                            e.currentTarget.style.borderColor = '#0f084b';
                        }}
                        onClick={handleInviteFriend}
                    >
                        Invite a Friend
                    </button>
                    <button
                        type="button"
                        className={cn('button-stroke', styles.action)}
                        style={{ 
                            textDecoration: 'none', 
                            borderColor: '#0f084b', 
                            cursor: copyStatus === 'copying' ? 'wait' : 'pointer',
                            minWidth: 140
                        }}
                        onMouseEnter={e => {
                            if (copyStatus !== 'copying') {
                                e.currentTarget.style.backgroundColor = '#0f084b';
                                e.currentTarget.style.color = '#ffffff';
                                e.currentTarget.style.borderColor = '#0f084b';
                            }
                        }}
                        onMouseLeave={e => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = '';
                            e.currentTarget.style.borderColor = '#0f084b';
                        }}
                        onClick={handleCopyReferralLink}
                        disabled={copyStatus === 'copying'}
                    >
                        {copyStatus === 'copying' ? 'Copying...' : 
                         copyStatus === 'copied' ? 'Copied!' : 
                         copyStatus === 'failed' ? 'Tap to copy manually' : 
                         'Copy Referral Link'}
                    </button>
                </div>
                {/* Fallback: show referral URL when clipboard copy fails on mobile */}
                {showReferralUrl && referralUrl && (
                    <div style={{ 
                        marginBottom: 16, 
                        padding: '12px 16px', 
                        background: '#f0f4f8', 
                        borderRadius: 8,
                        border: '1px solid #d1d5db'
                    }}>
                        <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 6 }}>
                            Your referral link (tap and hold to copy):
                        </div>
                        <input
                            type="text"
                            readOnly
                            value={referralUrl}
                            onClick={(e) => e.target.select()}
                            style={{
                                width: '100%',
                                padding: '10px 12px',
                                fontSize: 14,
                                border: '1px solid #d1d5db',
                                borderRadius: 6,
                                background: 'white',
                                color: '#0f084b',
                                fontFamily: 'monospace'
                            }}
                        />
                    </div>
                )}
                {activeTab === "Overview" && (
                    <div className={styles.overview}>
                        <div className={styles.head}>
                            <h1 className={cn("h2", styles.title)}>Creator Dashboard</h1>
                        </div>

                        <div className={styles.statsGrid}>
                            <div className={styles.statCard}>
                                <div className={styles.statValue}>0</div>
                                <div className={styles.statLabel}>Active Itineraries</div>
                            </div>
                            <div className={styles.statCard}>
                                <div className={styles.statValue}>{parseInt(creatorProfile?.completed_collabs, 10) || 0}</div>
                                <div className={styles.statLabel}>Collaborations</div>
                            </div>
                            <div className={styles.statCard}>
                                <div className={styles.statValue}>{creatorProfile?.view_count || 0}</div>
                                <div className={styles.statLabel}>Profile Views</div>
                            </div>
                            <div className={styles.statCard}>
                                <div className={styles.statValue}>0</div>
                                <div className={styles.statLabel}>Total Bookings</div>
                            </div>
                        </div>

                        <div className={styles.block}>
                            <h4 className={cn("h4", styles.blockTitle)}>Your Creator Profile</h4>
                            <div className={styles.profileFormGrid}>
                                {/* Left column - Avatar and basic info */}
                                <div className={styles.profileFormColumn}>
                                    {/* Avatar Upload */}
                                    <div>
                                        <label htmlFor="avatar-upload" style={{ 
                                            display: 'block', 
                                            width: '100%', 
                                            aspectRatio: '1', 
                                            borderRadius: 12, 
                                            overflow: 'hidden',
                                            border: '2px dashed #ddd',
                                            cursor: 'pointer',
                                            background: (avatarUploading || avatarUploadSuccess) ? '#f5f5f5' : profileForm.avatarUrl ? `url(${profileForm.avatarUrl}) center/cover` : '#f5f5f5',
                                            position: 'relative'
                                        }}>
                                            {avatarUploading ? (
                                                <div style={{ 
                                                    position: 'absolute', 
                                                    inset: 0, 
                                                    display: 'flex', 
                                                    flexDirection: 'column',
                                                    alignItems: 'center', 
                                                    justifyContent: 'center',
                                                    gap: 8
                                                }}>
                                                    <span style={{ fontSize: '16px', fontWeight: 600, color: '#dc1d08' }}>
                                                        Uploading and converting...
                                                    </span>
                                                    <small style={{ color: '#666' }}>Please wait</small>
                                                </div>
                                            ) : avatarUploadSuccess ? (
                                                <div style={{ 
                                                    position: 'absolute', 
                                                    inset: 0, 
                                                    display: 'flex', 
                                                    flexDirection: 'column',
                                                    alignItems: 'center', 
                                                    justifyContent: 'center',
                                                    gap: 8
                                                }}>
                                                    <span style={{ fontSize: '32px', color: '#10b981' }}>✓</span>
                                                    <span style={{ fontSize: '16px', fontWeight: 600, color: '#10b981' }}>
                                                        Avatar uploaded!
                                                    </span>
                                                </div>
                                            ) : !profileForm.avatarUrl && (
                                                <div style={{ 
                                                    position: 'absolute', 
                                                    inset: 0, 
                                                    display: 'flex', 
                                                    flexDirection: 'column',
                                                    alignItems: 'center', 
                                                    justifyContent: 'center',
                                                    color: '#666'
                                                }}>
                                                    <div style={{ fontSize: 48, marginBottom: 8 }}>📷</div>
                                                    <div style={{ fontSize: 14 }}>Upload Profile Picture</div>
                                                </div>
                                            )}
                                        </label>
                                        <input 
                                            id="avatar-upload" 
                                            type="file" 
                                            accept="image/*" 
                                            style={{ display: 'none' }} 
                                            onChange={handleAvatarChange}
                                        />
                                        <div style={{ 
                                            fontSize: 14, 
                                            color: '#666', 
                                            textAlign: 'center', 
                                            marginTop: 8 
                                        }}>
                                            Click image to update
                                        </div>
                                    </div>
                                    <IntroVideoUpload
                                        value={profileForm.introVideo}
                                        onChange={handleIntroVideoChange}
                                    />
                                    {introVideoSaveStatus && (
                                        <div style={{
                                            marginTop: 8,
                                            fontSize: 13,
                                            color: introVideoSaveStatus.includes('could not') ? '#dc1d08' : '#0f084b',
                                            textAlign: 'center'
                                        }}>
                                            {introVideoSaveStatus}
                                        </div>
                                    )}
                                    {creatorProfile?.profile_id && (
                                        <div style={{ marginTop: 16 }}>
                                            <ShowcaseUpload
                                                mode="dashboard"
                                                profileKind="creator"
                                                profileId={creatorProfile.profile_id}
                                                value={showcaseAssets}
                                                onChange={setShowcaseAssets}
                                            />
                                        </div>
                                    )}
                                    
                                    {/* Basic Info */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                        <div>
                                            <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>First Name</label>
                                            <input
                                                ref={firstNameInputRef}
                                                id="firstName-input"
                                                type="text"
                                                value={profileForm.firstName}
                                                onChange={(e) => handleProfileChange('firstName', e.target.value)}
                                                style={{ 
                                                    width: '100%', 
                                                    padding: '8px 12px', 
                                                    borderRadius: 8, 
                                                    border: '1px solid #ddd',
                                                    fontSize: 14
                                                }}
                                            />
                                        </div>
                                        <div>
                                            <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Last Name</label>
                                            <input
                                                ref={lastNameInputRef}
                                                id="lastName-input"
                                                type="text"
                                                value={profileForm.lastName}
                                                onChange={(e) => handleProfileChange('lastName', e.target.value)}
                                                style={{ 
                                                    width: '100%', 
                                                    padding: '8px 12px', 
                                                    borderRadius: 8, 
                                                    border: '1px solid #ddd',
                                                    fontSize: 14
                                                }}
                                            />
                                        </div>
                                        <div>
                                            <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>@Handle</label>
                                            <input
                                                ref={handleInputRef}
                                                id="handle-input"
                                                type="text"
                                                value={profileForm.handle}
                                                onChange={(e) => handleProfileChange('handle', e.target.value)}
                                                placeholder="yourhandle"
                                                style={{ 
                                                    width: '100%', 
                                                    padding: '8px 12px', 
                                                    borderRadius: 8, 
                                                    border: '1px solid #ddd',
                                                    fontSize: 14
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Right column - Detailed info */}
                                <div className={styles.profileFormColumnRight}>
                                    {/* About/Bio */}
                                    <div>
                                        <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>About You</label>
                                        <textarea
                                            ref={bioTextareaRef}
                                            id="bio-textarea"
                                            value={profileForm.bio}
                                            onChange={(e) => handleProfileChange('bio', e.target.value)}
                                            placeholder="Tell us about yourself, your travel style, and what you're looking for in collaborations..."
                                            style={{ 
                                                width: '100%', 
                                                minHeight: 120,
                                                padding: '8px 12px', 
                                                borderRadius: 8, 
                                                border: '1px solid #ddd',
                                                fontSize: 14,
                                                resize: 'vertical'
                                            }}
                                        />
                                    </div>
                                    
                                    {/* Location */}
                                    <div className={styles.formRow}>
                                        <div>
                                            <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Country</label>
                                            <LocationAutocomplete
                                                type="country"
                                                value={profileForm.locationCountry}
                                                onChange={handleCountryChange}
                                                placeholder="Start typing country..."
                                                inputId="location-country-input"
                                            />
                                        </div>
                                    </div>
                                    <div className={styles.formRow}>
                                        <div>
                                            <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>State</label>
                                            <LocationAutocomplete
                                                type="state"
                                                value={profileForm.locationState}
                                                onChange={(value) => handleProfileChange('locationState', value)}
                                                placeholder={profileForm.locationCountry === "United States" ? "Start typing state..." : "State (optional)"}
                                                disabled={profileForm.locationCountry && profileForm.locationCountry !== "United States"}
                                                inputId="location-state-input"
                                            />
                                        </div>
                                        <div>
                                            <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>City</label>
                                            <LocationAutocomplete
                                                type="city"
                                                value={profileForm.locationCity}
                                                onChange={(value) => handleProfileChange('locationCity', value)}
                                                stateValue={profileForm.locationState}
                                                placeholder={profileForm.locationState ? "Start typing city..." : profileForm.locationCountry === "United States" ? "Select state first" : "Start typing city..."}
                                                disabled={profileForm.locationCountry === "United States" && !profileForm.locationState}
                                                inputId="location-city-input"
                                            />
                                        </div>
                                    </div>
                                    
                                    {/* Demographics */}
                                    <div className={styles.formRow}>
                                        <div>
                                            <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Age Range</label>
                                            <select
                                                value={profileForm.ageRange}
                                                onChange={(e) => handleProfileChange('ageRange', e.target.value)}
                                                style={{ 
                                                    width: '100%', 
                                                    padding: '8px 12px', 
                                                    borderRadius: 8, 
                                                    border: '1px solid #ddd',
                                                    fontSize: 14
                                                }}
                                            >
                                                <option value="">Select...</option>
                                                <option value="18-24">18-24</option>
                                                <option value="25-34">25-34</option>
                                                <option value="35-44">35-44</option>
                                                <option value="45-54">45-54</option>
                                                <option value="55+">55+</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Gender</label>
                                            <select
                                                value={profileForm.gender}
                                                onChange={(e) => handleProfileChange('gender', e.target.value)}
                                                style={{ 
                                                    width: '100%', 
                                                    padding: '8px 12px', 
                                                    borderRadius: 8, 
                                                    border: '1px solid #ddd',
                                                    fontSize: 14
                                                }}
                                            >
                                                <option value="">Select...</option>
                                                <option value="female">Female</option>
                                                <option value="male">Male</option>
                                                <option value="non-binary">Other</option>
                                                <option value="prefer-not-to-say">Prefer not to say</option>
                                            </select>
                                        </div>
                                    </div>
                                    
                                    {/* Social Media */}
                                    <div>
                                        <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 8 }}>Social Media</label>
                                        <div className={styles.formRowSocial}>
                                            <input
                                                type="text"
                                                value={profileForm.instagram}
                                                onChange={(e) => handleProfileChange('instagram', e.target.value)}
                                                onBlur={() => handleUrlBlur('instagram')}
                                                placeholder="Instagram URL"
                                                style={{ 
                                                    width: '100%', 
                                                    padding: '8px 12px', 
                                                    borderRadius: 8, 
                                                    border: '1px solid #ddd',
                                                    fontSize: 14
                                                }}
                                            />
                                            <input
                                                type="text"
                                                value={profileForm.tiktok}
                                                onChange={(e) => handleProfileChange('tiktok', e.target.value)}
                                                onBlur={() => handleUrlBlur('tiktok')}
                                                placeholder="TikTok URL"
                                                style={{ 
                                                    width: '100%', 
                                                    padding: '8px 12px', 
                                                    borderRadius: 8, 
                                                    border: '1px solid #ddd',
                                                    fontSize: 14
                                                }}
                                            />
                                            <input
                                                type="text"
                                                value={profileForm.youtube}
                                                onChange={(e) => handleProfileChange('youtube', e.target.value)}
                                                onBlur={() => handleUrlBlur('youtube')}
                                                placeholder="YouTube URL"
                                                style={{ 
                                                    width: '100%', 
                                                    padding: '8px 12px', 
                                                    borderRadius: 8, 
                                                    border: '1px solid #ddd',
                                                    fontSize: 14
                                                }}
                                            />
                                            <input
                                                type="text"
                                                value={profileForm.other}
                                                onChange={(e) => handleProfileChange('other', e.target.value)}
                                                onBlur={() => handleUrlBlur('other')}
                                                placeholder="Other website/link"
                                                style={{ 
                                                    width: '100%', 
                                                    padding: '8px 12px', 
                                                    borderRadius: 8, 
                                                    border: '1px solid #ddd',
                                                    fontSize: 14
                                                }}
                                            />
                                        </div>
                                    </div>
                                    
                                    {/* Categories */}
                                    <div>
                                        <label className={styles.formLabel}>Content Categories</label>
                                        <div className={styles.formButtonGroup}>
                                            {CATEGORIES.map(cat => {
                                                const isSelected = profileForm.categories.includes(cat.key);
                                                const isDisabled = !isSelected && profileForm.categories.length >= 3;
                                                return (
                                                    <button
                                                        key={cat.key}
                                                        type="button"
                                                        onClick={() => handleCategoryToggle(cat.key)}
                                                        disabled={isDisabled}
                                                        className={cn(styles.formButton, { [styles.formButtonActive]: isSelected })}
                                                        style={isDisabled && !isSelected ? { color: '#ccc' } : undefined}
                                                    >
                                                        {cat.label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        {profileForm.categories.includes('other') && (
                                            <div style={{ marginTop: 8 }}>
                                                <input
                                                    type="text"
                                                    value={profileForm.nicheOther}
                                                    onChange={(e) => handleProfileChange('nicheOther', e.target.value)}
                                                    placeholder="Please specify..."
                                                    className={styles.formInput}
                                                />
                                            </div>
                                        )}
                                        <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>
                                            {profileForm.categories.length}/3 selected
                                        </div>
                                    </div>

                                    {/* Preferences */}
                                    <div>
                                        <label className={styles.formLabel}>Collaboration Preferences</label>
                                        <div className={styles.formButtonGroup}>
                                            {PREFS.map(pref => {
                                                const isSelected = profileForm.preferences.includes(pref.key);
                                                return (
                                                    <button
                                                        key={pref.key}
                                                        type="button"
                                                        onClick={() => handlePreferenceToggle(pref.key)}
                                                        className={cn(styles.formButton, { [styles.formButtonActive]: isSelected })}
                                                    >
                                                        {pref.label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                    
                                    {/* Past Collaborations */}
                                    <div>
                                        <label style={{ fontSize: 12, color: '#666', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: 8 }}>
                                            Past Collaboration Partners
                                            <Tippy content="Enter any previous hosts, hotels or tourism boards you have collaborated with">
                                                <span style={{ display: 'inline-flex', cursor: 'help' }}>
                                                    <Icon name="info" size={14} />
                                                </span>
                                            </Tippy>
                                        </label>
                                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                                            {profileForm.partners.map((partner, index) => (
                                                <div
                                                    key={index}
                                                    className={styles.partnerTag}
                                                >
                                                    <span>{partner}</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemovePartner(index)}
                                                        style={{
                                                            background: 'transparent',
                                                            border: 'none',
                                                            cursor: 'pointer',
                                                            padding: 0,
                                                            fontSize: 14,
                                                            color: '#666',
                                                            display: 'flex',
                                                            alignItems: 'center'
                                                        }}
                                                    >
                                                        ✕
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                        <div className={styles.partnerInputGroup}>
                                            <input
                                                ref={partnerInputRef}
                                                id="partner-input"
                                                type="text"
                                                value={profileForm.partnerInput}
                                                onChange={(e) => handleProfileChange('partnerInput', e.target.value)}
                                                onKeyPress={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        handleAddPartner();
                                                    }
                                                }}
                                                placeholder="Add partner name (e.g., @cabinname)"
                                                className={styles.formInput}
                                            />
                                            <button
                                                type="button"
                                                onClick={handleAddPartner}
                                                className={styles.formButtonAdd}
                                            >
                                                Add
                                            </button>
                                        </div>
                                    </div>
                                    
                                    {/* Media Kit - PDF Upload or URL */}
                                    <div>
                                        <label className={styles.formLabel}>
                                            Media Kit (PDF Upload or URL)
                                        </label>

                                        <div style={{ display: 'flex', gap: '12px', flexDirection: 'column', marginBottom: 8 }}>
                                            {/* PDF Upload Option */}
                                            <div className={styles.mediaKitUploadRow}>
                                                <input
                                                    type="file"
                                                    accept="application/pdf"
                                                    onChange={handleMediaKitFileChange}
                                                    disabled={mediaKitUploading}
                                                    style={{ display: 'none' }}
                                                    id="media-kit-upload-dashboard"
                                                />
                                                <label
                                                    htmlFor="media-kit-upload-dashboard"
                                                    style={{
                                                        display: 'inline-block',
                                                        padding: '8px 16px',
                                                        background: '#0f084b',
                                                        color: 'white',
                                                        borderRadius: 8,
                                                        cursor: mediaKitUploading ? 'not-allowed' : 'pointer',
                                                        fontSize: 14,
                                                        fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                                                        opacity: mediaKitUploading ? 0.6 : 1
                                                    }}
                                                >
                                                    {mediaKitUploading ? 'Uploading...' : 'Upload PDF Media Kit'}
                                                </label>
                                                {profileForm.mediaKitUrl && (profileForm.mediaKitUrl.endsWith('.pdf') || profileForm.mediaKitUrl.includes('blob.vercel-storage.com')) && (
                                                    <a
                                                        href={profileForm.mediaKitUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{
                                                            color: '#0f084b',
                                                            textDecoration: 'underline',
                                                            fontSize: 14
                                                        }}
                                                    >
                                                        View Current PDF
                                                    </a>
                                                )}
                                            </div>

                                            {/* OR divider */}
                                            <div style={{ textAlign: 'center', color: '#666', fontSize: 12 }}>
                                                OR
                                            </div>

                                            {/* URL Input Option */}
                                            <input
                                                type="text"
                                                value={profileForm.mediaKitUrl}
                                                onChange={(e) => handleProfileChange('mediaKitUrl', e.target.value)}
                                                onBlur={() => handleUrlBlur('mediaKitUrl')}
                                                placeholder="https://your-media-kit-link.com"
                                                className={styles.formInput}
                                            />
                                        </div>
                                    </div>
                                    
                                    {/* Save Button */}
                                    <div className={styles.formButtonSaveContainer}>
                                        <button
                                            onClick={() => handleSaveProfile()}
                                            disabled={savingProfile}
                                            className={styles.formButtonSave}
                                            style={savingProfile ? { background: '#999', cursor: 'not-allowed' } : undefined}
                                        >
                                            {savingProfile ? 'Saving...' : 'Save Profile'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                {activeTab === "Profile" && (
                    <div className={styles.placeholder}>
                        Redirecting to profile editor...
                        {typeof window !== 'undefined' && 
                            setTimeout(() => {
                                window.location.href = `/creator/onboarding?email=${encodeURIComponent(email)}`;
                            }, 1000)
                        }
                    </div>
                )}
                {activeTab === "Itineraries" && (
                    <div className={styles.placeholder}>
                        Redirecting to itineraries...
                        {typeof window !== 'undefined' && 
                            setTimeout(() => {
                                window.location.href = `/itineraries?email=${encodeURIComponent(email)}`;
                            }, 1000)
                        }
                    </div>
                )}
                {activeTab === "Messages" && (
                    <div className={styles.placeholder}>
                        Redirecting to messages...
                        {typeof window !== 'undefined' && 
                            setTimeout(() => {
                                window.location.href = `/messages`;
                            }, 500)
                        }
                    </div>
                )}
                {activeTab === "Collaborations" && (
                    <div className={styles.placeholder}>
                        Collaborations coming soon
                    </div>
                )}
                {activeTab === "Reviews" && (
                    <div>
                        <div style={{ marginBottom: '32px' }}>
                            <h3 style={{ marginBottom: '16px', fontSize: '18px', fontWeight: '600' }}>Pending Reviews</h3>
                            <PendingReviews userType="creator" />
                        </div>
                        <div>
                            <h3 style={{ marginBottom: '16px', fontSize: '18px', fontWeight: '600' }}>Your Reviews</h3>
                            <CreatorReviews />
                        </div>
                    </div>
                )}
                {activeTab === "Analytics" && (
                    <div className={styles.placeholder}>
                        Analytics coming soon
                    </div>
                )}
                {activeTab === "Settings" && <Settings userType="creator" />}
            </div>
        </div>
    );

    return (
        <InfoPage 
            title={`Creator Dashboard${activeTab !== 'Overview' ? ' - ' + activeTab : ''}`}
            below={dashboardContent}
            tallOverride={false}
            customMinHeight={800}
            disableStamps
        />
    );
};

export default CreatorDashboard;
